import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faBell, faChevronDown, faChevronUp, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Alert from "../../../components/common/Alert";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import { useAuth } from "../../../contexts/AuthContext";
import alertsService from "../../../services/alertsService";
import MessagingService from "../../../services/core/MessagingService";
import "./AlertDefinitionsPage.css";

const parseParams = (json) => {
  try {
    return JSON.parse(json || "{}");
  } catch {
    return {};
  }
};

// A superuser's recipient list mixes users/groups from every tenant - without
// this, e.g. several unrelated "Todos" groups are indistinguishable. Only
// label with the tenant when more than one is actually present, so a normal
// (single-tenant) user doesn't see redundant noise on every row.
const hasMultipleTenants = (items) => new Set((items || []).map((i) => i.tenantId).filter(Boolean)).size > 1;
const userLabel = (u, showTenant) =>
  `${u.displayName || u.email}${showTenant && u.tenantName ? ` · ${u.tenantName}` : ""}`;
const groupLabel = (g, showTenant) =>
  `${g.name} (${g.memberCount})${showTenant && g.tenantName ? ` · ${g.tenantName}` : ""}`;

const AlertDefinitionCard = ({ definition, canManage, users, groups, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(definition);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runLog, setRunLog] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => setForm(definition), [definition]);

  const showUserTenant = useMemo(() => hasMultipleTenants(users), [users]);
  const showGroupTenant = useMemo(() => hasMultipleTenants(groups), [groups]);
  const params = useMemo(() => parseParams(form.params), [form.params]);
  const setParam = (key, value) =>
    setForm((current) => ({ ...current, params: JSON.stringify({ ...parseParams(current.params), [key]: value }) }));

  const toggleGroupId = (groupId) =>
    setForm((current) => {
      const exists = (current.recipientGroupIds || []).includes(groupId);
      return {
        ...current,
        recipientGroupIds: exists
          ? current.recipientGroupIds.filter((id) => id !== groupId)
          : [...(current.recipientGroupIds || []), groupId],
      };
    });

  const toggleUserId = (userId) =>
    setForm((current) => {
      const exists = (current.recipientUserIds || []).includes(userId);
      return {
        ...current,
        recipientUserIds: exists
          ? current.recipientUserIds.filter((id) => id !== userId)
          : [...(current.recipientUserIds || []), userId],
      };
    });

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const saved = await alertsService.update(form.alertCode, {
        enabled: form.enabled,
        cron: form.cron,
        channelEmail: form.channelEmail,
        channelMessage: form.channelMessage,
        recipientUserIds: form.recipientUserIds || [],
        recipientGroupIds: form.recipientGroupIds || [],
        recipientEmails: form.recipientEmails || [],
        params: form.params,
        version: form.version || 0,
      });
      setForm(saved);
      setSuccess(t("alerts.saved"));
      onSaved?.(saved);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError("");
    setSuccess("");
    try {
      await alertsService.runNow(form.alertCode);
      setSuccess(t("alerts.runTriggered"));
      await loadRunLog();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setRunning(false);
    }
  };

  const loadRunLog = useCallback(async () => {
    try {
      const page = await alertsService.runLog(form.alertCode, 0, 10);
      setRunLog(page?.content || []);
    } catch {
      setRunLog([]);
    }
  }, [form.alertCode]);

  useEffect(() => {
    if (expanded && form.configured && runLog === null) {
      loadRunLog();
    }
  }, [expanded, form.configured, runLog, loadRunLog]);

  const emailRecipientCount = (form.recipientEmails || []).length + (form.recipientUserIds || []).length;
  const messageRecipientCount = (form.recipientUserIds || []).length + (form.recipientGroupIds || []).length;

  return (
    <div className={`alert-card ${form.enabled ? "is-enabled" : ""}`}>
      <div className="alert-card-header" onClick={() => setExpanded((v) => !v)}>
        <div>
          <strong>{form.displayLabel}</strong>
          <span className="alert-card-status">
            {form.enabled ? t("alerts.status.enabled") : t("alerts.status.disabled")}
          </span>
        </div>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
      </div>

      {expanded && (
        <div className="alert-card-body">
          {error && <Alert type="error" message={error} onClose={() => setError("")} />}
          {success && <Alert type="success" message={success} onClose={() => setSuccess("")} />}

          <label className="alert-toggle">
            <input
              type="checkbox"
              checked={!!form.enabled}
              disabled={!canManage}
              onChange={(e) => setForm((c) => ({ ...c, enabled: e.target.checked }))}
            />
            {t("alerts.fields.enabled")}
          </label>

          <label>
            <span>{t("alerts.fields.cron")}</span>
            <input
              className="form-control"
              value={form.cron || ""}
              disabled={!canManage}
              onChange={(e) => setForm((c) => ({ ...c, cron: e.target.value }))}
            />
            <small>{t("alerts.fields.cronHint")}</small>
          </label>

          <div className="alert-channels">
            <label>
              <input
                type="checkbox"
                checked={!!form.channelEmail}
                disabled={!canManage}
                onChange={(e) => setForm((c) => ({ ...c, channelEmail: e.target.checked }))}
              />
              {t("alerts.fields.channelEmail")} ({emailRecipientCount})
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!form.channelMessage}
                disabled={!canManage}
                onChange={(e) => setForm((c) => ({ ...c, channelMessage: e.target.checked }))}
              />
              {t("alerts.fields.channelMessage")} ({messageRecipientCount})
            </label>
          </div>

          <label>
            <span>{t("alerts.fields.extraEmails")}</span>
            <input
              className="form-control"
              placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
              value={(form.recipientEmails || []).join(", ")}
              disabled={!canManage}
              onChange={(e) =>
                setForm((c) => ({
                  ...c,
                  recipientEmails: e.target.value
                    .split(/[,;]/)
                    .map((v) => v.trim())
                    .filter(Boolean),
                }))
              }
            />
          </label>

          <div className="alert-recipients">
            <div className="alert-recipients-column">
              <strong>{t("alerts.fields.recipientUsers")}</strong>
              <div className="alert-recipients-list">
                {users.map((u) => (
                  <label key={u.userId}>
                    <input
                      type="checkbox"
                      disabled={!canManage}
                      checked={(form.recipientUserIds || []).includes(u.userId)}
                      onChange={() => toggleUserId(u.userId)}
                    />
                    {userLabel(u, showUserTenant)}
                  </label>
                ))}
              </div>
            </div>
            <div className="alert-recipients-column">
              <strong>{t("alerts.fields.recipientGroups")}</strong>
              <div className="alert-recipients-list">
                {groups.map((g) => (
                  <label key={g.id}>
                    <input
                      type="checkbox"
                      disabled={!canManage}
                      checked={(form.recipientGroupIds || []).includes(g.id)}
                      onChange={() => toggleGroupId(g.id)}
                    />
                    {groupLabel(g, showGroupTenant)}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {("lookbackDays" in params || "minAgeDays" in params) && (
            <div className="alert-advanced-params">
              <label>
                <span>{t("alerts.fields.lookbackDays")}</span>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  disabled={!canManage}
                  value={params.lookbackDays ?? 30}
                  onChange={(e) => setParam("lookbackDays", Number(e.target.value))}
                />
              </label>
              <label>
                <span>{t("alerts.fields.minAgeDays")}</span>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  disabled={!canManage}
                  value={params.minAgeDays ?? 2}
                  onChange={(e) => setParam("minAgeDays", Number(e.target.value))}
                />
              </label>
            </div>
          )}

          {canManage && (
            <div className="alert-actions">
              <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
                {t("common.save")}
              </button>
              <button type="button" className="btn btn-default" disabled={running} onClick={runNow}>
                <FontAwesomeIcon icon={faPlay} /> <span>{t("alerts.runNow")}</span>
              </button>
            </div>
          )}

          {runLog && runLog.length > 0 && (
            <table className="alert-run-log">
              <thead>
                <tr>
                  <th>{t("alerts.runLog.startedAt")}</th>
                  <th>{t("alerts.runLog.status")}</th>
                  <th>{t("alerts.runLog.matched")}</th>
                  <th>{t("alerts.runLog.email")}</th>
                  <th>{t("alerts.runLog.message")}</th>
                </tr>
              </thead>
              <tbody>
                {runLog.map((run) => (
                  <tr key={run.id}>
                    <td>{run.startedAt ? new Date(run.startedAt).toLocaleString() : ""}</td>
                    <td>{run.status}</td>
                    <td>{run.matchedCount}</td>
                    <td>{run.emailSent ? "✓" : ""}</td>
                    <td>{run.messageSent ? "✓" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const STATUS_LABEL_KEYS = {
  PENDING: "alerts.pending.statusPending",
  APPROVED: "alerts.pending.statusApproved",
  REJECTED: "alerts.pending.statusRejected",
};

// AX-style relative date wildcards used inside a saved filter's stored values,
// e.g. "(Today-30)" or "(Today-30)..(Today)" - see RelativeDateExpressions on
// the backend, which is what actually resolves these on every scheduled run.
// Parens are optional on each side independently - matches RelativeDateExpressions.java on the
// backend, which resolves "(Today-10)..Today" (or any other mix of parenthesized/bare tokens)
// just as leniently, so a saved wildcard is always recognized here for editing too.
const RELATIVE_RANGE_RE = /^\(?\s*today\s*([+-]\s*\d+)?\s*\)?\.\.\(?\s*today\s*([+-]\s*\d+)?\s*\)?$/i;
const RELATIVE_SINGLE_RE = /^\(?\s*today\s*([+-]\s*\d+)?\s*\)?$/i;

const parseOffset = (group) => (group ? parseInt(group.replace(/\s+/g, ""), 10) : 0);
const formatRelativeToken = (offset) => (offset === 0 ? "(Today)" : `(Today${offset > 0 ? "+" : ""}${offset})`);

const detectRelativeDateFields = (filtersJson) => {
  let parsed = {};
  try {
    parsed = JSON.parse(filtersJson || "{}");
  } catch {
    return {};
  }
  const result = {};
  Object.entries(parsed).forEach(([key, value]) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    const rangeMatch = trimmed.match(RELATIVE_RANGE_RE);
    if (rangeMatch) {
      result[key] = { type: "range", from: parseOffset(rangeMatch[1]), to: parseOffset(rangeMatch[2]) };
      return;
    }
    const singleMatch = trimmed.match(RELATIVE_SINGLE_RE);
    if (singleMatch) {
      result[key] = { type: "single", offset: parseOffset(singleMatch[1]) };
    }
  });
  return result;
};

const buildEditableForm = (alert) => ({
  cron: alert.cron,
  channelEmail: alert.channelEmail,
  channelMessage: alert.channelMessage,
  recipientUserIds: alert.recipientUserIds || [],
  recipientGroupIds: alert.recipientGroupIds || [],
  recipientEmails: alert.recipientEmails || [],
  dateFields: detectRelativeDateFields(alert.filters),
});

const SavedFilterAlertCard = ({
  alert,
  canManage,
  users,
  groups,
  comment,
  onCommentChange,
  busy,
  onToggleEnabled,
  onDecide,
  onDelete,
  onSave,
  onRunNow,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(() => buildEditableForm(alert));
  const showUserTenant = useMemo(() => hasMultipleTenants(users), [users]);
  const showGroupTenant = useMemo(() => hasMultipleTenants(groups), [groups]);

  useEffect(() => {
    setForm(buildEditableForm(alert));
  }, [alert]);

  const updateFieldOffset = (key, patch) =>
    setForm((current) => ({
      ...current,
      dateFields: { ...current.dateFields, [key]: { ...current.dateFields[key], ...patch } },
    }));

  const toggleUserId = (userId) =>
    setForm((current) => ({
      ...current,
      recipientUserIds: current.recipientUserIds.includes(userId)
        ? current.recipientUserIds.filter((id) => id !== userId)
        : [...current.recipientUserIds, userId],
    }));

  const toggleGroupId = (groupId) =>
    setForm((current) => ({
      ...current,
      recipientGroupIds: current.recipientGroupIds.includes(groupId)
        ? current.recipientGroupIds.filter((id) => id !== groupId)
        : [...current.recipientGroupIds, groupId],
    }));

  const save = () => {
    let parsedFilters = {};
    try {
      parsedFilters = JSON.parse(alert.filters || "{}");
    } catch {
      parsedFilters = {};
    }
    Object.entries(form.dateFields).forEach(([key, cfg]) => {
      parsedFilters[key] =
        cfg.type === "range"
          ? `${formatRelativeToken(cfg.from)}..${formatRelativeToken(cfg.to)}`
          : formatRelativeToken(cfg.offset);
    });
    onSave({
      cron: form.cron,
      channelEmail: form.channelEmail,
      channelMessage: form.channelMessage,
      recipientUserIds: form.recipientUserIds,
      recipientGroupIds: form.recipientGroupIds,
      recipientEmails: form.recipientEmails,
      filters: JSON.stringify(parsedFilters),
    });
  };

  const dateFieldEntries = Object.entries(form.dateFields);
  const emailRecipientCount = form.recipientEmails.length + form.recipientUserIds.length;
  const messageRecipientCount = form.recipientUserIds.length + form.recipientGroupIds.length;

  return (
    <div className={`alert-card ${alert.enabled ? "is-enabled" : ""}`}>
      <div className="alert-card-header" onClick={() => setExpanded((v) => !v)}>
        <div>
          <strong>{alert.name}</strong>
          <span className="alert-card-status">
            {alert.entityKey} · {t(STATUS_LABEL_KEYS[alert.status] || alert.status)}
          </span>
        </div>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
      </div>

      {expanded && (
        <div className="alert-card-body">
          <p className="pending-alert-card-meta">
            {canManage &&
              `${
                alert.tenantName
                  ? t("alerts.pending.requestedByWithTenant", {
                      name: alert.requestedByName || alert.requestedBy,
                      tenant: alert.tenantName,
                    })
                  : t("alerts.pending.requestedBy", { name: alert.requestedByName || alert.requestedBy })
              } · `}
            {alert.requestedAt ? new Date(alert.requestedAt).toLocaleString() : ""}
            {" · "}
            {t("alerts.saveAsAlert.subject")}: {alert.subject}
            {" · "}
            {alert.lastRunAt
              ? t("alerts.pending.lastRunAt", { date: new Date(alert.lastRunAt).toLocaleString() })
              : t("alerts.pending.neverRun")}
          </p>

          <label className="alert-toggle">
            <input type="checkbox" checked={!!alert.enabled} disabled={busy} onChange={onToggleEnabled} />
            {t("alerts.pending.enabled")}
          </label>

          <label>
            <span>{t("alerts.fields.cron")}</span>
            <input
              className="form-control"
              value={form.cron}
              onChange={(e) => setForm((c) => ({ ...c, cron: e.target.value }))}
            />
            <small>{t("alerts.fields.cronHint")}</small>
          </label>

          {dateFieldEntries.length > 0 && (
            <div className="pending-alert-date-fields">
              <strong>{t("alerts.pending.dateWildcards")}</strong>
              {dateFieldEntries.map(([key, cfg]) => (
                <div key={key} className="pending-alert-date-field-row">
                  <span>{key}</span>
                  {cfg.type === "range" ? (
                    <>
                      <label>
                        {t("alerts.fields.lookbackDays")}
                        <input
                          type="number"
                          className="form-control"
                          value={-cfg.from}
                          onChange={(e) => updateFieldOffset(key, { from: -Number(e.target.value) })}
                        />
                      </label>
                      <label>
                        {t("alerts.fields.minAgeDays")}
                        <input
                          type="number"
                          className="form-control"
                          value={-cfg.to}
                          onChange={(e) => updateFieldOffset(key, { to: -Number(e.target.value) })}
                        />
                      </label>
                    </>
                  ) : (
                    <label>
                      {t("alerts.pending.daysOffset")}
                      <input
                        type="number"
                        className="form-control"
                        value={-cfg.offset}
                        onChange={(e) => updateFieldOffset(key, { offset: -Number(e.target.value) })}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="alert-channels">
            <label>
              <input
                type="checkbox"
                checked={!!form.channelEmail}
                onChange={(e) => setForm((c) => ({ ...c, channelEmail: e.target.checked }))}
              />
              {t("alerts.fields.channelEmail")} ({emailRecipientCount})
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!form.channelMessage}
                onChange={(e) => setForm((c) => ({ ...c, channelMessage: e.target.checked }))}
              />
              {t("alerts.fields.channelMessage")} ({messageRecipientCount})
            </label>
          </div>

          <label>
            <span>{t("alerts.fields.extraEmails")}</span>
            <input
              className="form-control"
              placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
              value={form.recipientEmails.join(", ")}
              onChange={(e) =>
                setForm((c) => ({
                  ...c,
                  recipientEmails: e.target.value.split(/[,;]/).map((v) => v.trim()).filter(Boolean),
                }))
              }
            />
          </label>

          <div className="alert-recipients">
            <div className="alert-recipients-column">
              <strong>{t("alerts.fields.recipientUsers")}</strong>
              <div className="alert-recipients-list">
                {users.map((u) => (
                  <label key={u.userId}>
                    <input
                      type="checkbox"
                      checked={form.recipientUserIds.includes(u.userId)}
                      onChange={() => toggleUserId(u.userId)}
                    />
                    {userLabel(u, showUserTenant)}
                  </label>
                ))}
              </div>
            </div>
            <div className="alert-recipients-column">
              <strong>{t("alerts.fields.recipientGroups")}</strong>
              <div className="alert-recipients-list">
                {groups.map((g) => (
                  <label key={g.id}>
                    <input
                      type="checkbox"
                      checked={form.recipientGroupIds.includes(g.id)}
                      onChange={() => toggleGroupId(g.id)}
                    />
                    {groupLabel(g, showGroupTenant)}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="alert-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
              {t("common.save")}
            </button>
            {alert.status === "APPROVED" && (
              <button type="button" className="btn btn-default" disabled={busy} onClick={onRunNow}>
                <FontAwesomeIcon icon={faPlay} /> <span>{t("alerts.runNow")}</span>
              </button>
            )}
            {canManage && alert.status === "PENDING" && (
              <input
                className="form-control"
                placeholder={t("alerts.pending.commentPlaceholder")}
                value={comment || ""}
                onChange={(e) => onCommentChange(e.target.value)}
              />
            )}
            {canManage && alert.status === "PENDING" && (
              <>
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onDecide(true)}>
                  {t("alerts.pending.approve")}
                </button>
                <button type="button" className="btn btn-default" disabled={busy} onClick={() => onDecide(false)}>
                  {t("alerts.pending.reject")}
                </button>
              </>
            )}
            <button type="button" className="btn btn-default" disabled={busy} onClick={onDelete}>
              {t("common.delete")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SavedFilterAlertsSection = ({ canManage, users, groups }) => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [comments, setComments] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = canManage
        ? await alertsService.allSavedFilterAlerts()
        : await alertsService.mySavedFilterAlerts();
      setAlerts(rows || []);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [canManage, t]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, approved) => {
    setBusyId(id);
    try {
      await alertsService.decideSavedFilterAlert(id, { approved, comment: comments[id] || "" });
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = async (alert) => {
    setBusyId(alert.id);
    try {
      await alertsService.updateSavedFilterAlert(alert.id, { enabled: !alert.enabled });
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusyId(null);
    }
  };

  const saveAlert = async (id, body) => {
    setBusyId(id);
    try {
      await alertsService.updateSavedFilterAlert(id, body);
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (alert) => {
    setBusyId(alert.id);
    setSuccess("");
    try {
      await alertsService.runNowSavedFilterAlert(alert.id);
      setSuccess(t("alerts.runTriggered"));
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (alert) => {
    if (!window.confirm(t("alerts.pending.confirmDelete", { name: alert.name }))) return;
    setBusyId(alert.id);
    try {
      await alertsService.deleteSavedFilterAlert(alert.id);
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="alerts-page" style={{ marginTop: 24 }}>
      <h2>{canManage ? t("alerts.pending.titleAll") : t("alerts.pending.titleMine")}</h2>
      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess("")} />}
      {loading ? (
        <Spinner size="large" message={t("common.loading")} />
      ) : alerts.length === 0 ? (
        <p>{t("alerts.pending.empty")}</p>
      ) : (
        alerts.map((alert) => (
          <SavedFilterAlertCard
            key={alert.id}
            alert={alert}
            canManage={canManage}
            users={users}
            groups={groups}
            comment={comments[alert.id]}
            onCommentChange={(value) => setComments((c) => ({ ...c, [alert.id]: value }))}
            busy={busyId === alert.id}
            onToggleEnabled={() => toggleEnabled(alert)}
            onDecide={(approved) => decide(alert.id, approved)}
            onDelete={() => remove(alert)}
            onSave={(body) => saveAlert(alert.id, body)}
            onRunNow={() => runNow(alert)}
          />
        ))
      )}
    </div>
  );
};

export default function AlertDefinitionsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("alerts.manage");
  const [definitions, setDefinitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [catalog, userRows, groupRows] = await Promise.all([
        alertsService.catalog(),
        MessagingService.users(),
        MessagingService.groups(),
      ]);
      setDefinitions(catalog || []);
      setUsers((userRows || []).filter((u) => u.messagingEnabled !== false));
      setGroups(groupRows || []);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = (saved) =>
    setDefinitions((current) => current.map((d) => (d.alertCode === saved.alertCode ? saved : d)));

  return (
    <div className="alerts-page">
      <PageHeader title={t("alerts.title")} subtitle={t("alerts.subtitle")} icon={faBell} />
      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      {loading ? (
        <Spinner size="large" message={t("common.loading")} />
      ) : (
        <div className="alert-cards">
          {definitions.map((definition) => (
            <AlertDefinitionCard
              key={definition.alertCode}
              definition={definition}
              canManage={canManage}
              users={users}
              groups={groups}
              onSaved={onSaved}
            />
          ))}
          {definitions.length === 0 && <p>{t("alerts.empty")}</p>}
        </div>
      )}
      <SavedFilterAlertsSection canManage={canManage} users={users} groups={groups} />
    </div>
  );
}
